import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Switch, InputNumber, message, Progress, Spin, Empty, Tooltip, Popconfirm, QRCode } from 'antd';
import {
  CloudUploadOutlined,
  InboxOutlined,
  CopyOutlined,
  DownloadOutlined,
  DeleteOutlined,
  LinkOutlined,
  QrcodeOutlined,
  SettingOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  FileOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { cloudDiskAPI } from '../services/api';
import { copyToClipboard } from '../utils/clipboard';
import './CloudDisk.css';

interface UploadRecord {
  shareCode: string;
  shareUrl: string;
  originalName: string;
  fileSize: number;
  fileType: string;
  extractionCode: string;
  maxDownloads: number;
  createdAt: string;
}

const STORAGE_KEY = 'cloud_disk_upload_records';
const MAX_RECORDS = 50;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const loadRecords = (): UploadRecord[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveRecords = (records: UploadRecord[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(0, MAX_RECORDS)));
  } catch (e) {
    console.error('保存上传记录失败:', e);
  }
};

const CloudDisk: React.FC = () => {
  const navigate = useNavigate();
  const [maxFileSizeMB, setMaxFileSizeMB] = useState(100);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [enablePassword, setEnablePassword] = useState(false);
  const [extractionCode, setExtractionCode] = useState('');
  const [enableMaxDownloads, setEnableMaxDownloads] = useState(false);
  const [maxDownloads, setMaxDownloads] = useState<number>(10);
  const [currentShare, setCurrentShare] = useState<UploadRecord | null>(null);
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrRecord, setQrRecord] = useState<UploadRecord | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    fetchConfig();
    setRecords(loadRecords());
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await cloudDiskAPI.getPublicConfig();
      if (res.data?.status === 200 && res.data?.data?.max_file_size_mb) {
        setMaxFileSizeMB(res.data.data.max_file_size_mb);
      }
    } catch (e) {
      // 使用默认值
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      message.error(`文件大小超过限制（最大${maxFileSizeMB}MB）`);
      return false;
    }

    if (enablePassword && !extractionCode.trim()) {
      message.warning('已开启提取码，请输入提取码');
      return false;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await cloudDiskAPI.upload(
        file,
        {
          extractionCode: enablePassword ? extractionCode.trim() : undefined,
          maxDownloads: enableMaxDownloads ? maxDownloads : 0,
        },
        (p) => setUploadProgress(p)
      );
      if (res.data?.status === 200) {
        const shareCode = res.data.share_code;
        const shareUrl = `${window.location.origin}/cloud-disk/share/${shareCode}`;
        const record: UploadRecord = {
          shareCode,
          shareUrl,
          originalName: res.data.original_name,
          fileSize: res.data.file_size,
          fileType: res.data.file_type,
          extractionCode: res.data.extraction_code || '',
          maxDownloads: res.data.max_downloads || 0,
          createdAt: new Date().toISOString(),
        };
        setCurrentShare(record);
        const newRecords = [record, ...records].slice(0, MAX_RECORDS);
        setRecords(newRecords);
        saveRecords(newRecords);
        message.success('上传成功，已生成分享链接');
      } else {
        message.error(res.data?.error || '上传失败');
      }
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message || '上传失败');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
    return false;
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    e.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [maxFileSizeMB, enablePassword, extractionCode, enableMaxDownloads, maxDownloads, records]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleCopyLink = async (url: string) => {
    const ok = await copyToClipboard(url);
    if (ok) message.success('链接已复制');
    else message.error('复制失败');
  };

  const handleDeleteRecord = (shareCode: string) => {
    const newRecords = records.filter(r => r.shareCode !== shareCode);
    setRecords(newRecords);
    saveRecords(newRecords);
    if (currentShare?.shareCode === shareCode) {
      setCurrentShare(null);
    }
    message.success('已从本地记录删除');
  };

  const handleShowQR = (record: UploadRecord) => {
    setQrRecord(record);
    setQrModalVisible(true);
  };

  const handleDownloadOwn = (record: UploadRecord) => {
    const url = cloudDiskAPI.getDownloadUrl(record.shareCode, record.extractionCode || undefined);
    window.open(url, '_blank');
  };

  return (
    <div className="cloud-disk-page">
      <div className="cloud-disk-container">
        <div className="cloud-disk-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            className="cloud-disk-back"
          >
            返回首页
          </Button>
          <h1 className="cloud-disk-title">
            <CloudUploadOutlined /> Tomato 云盘
          </h1>
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={fetchConfig}
            loading={loading}
          />
        </div>

        <Spin spinning={loading}>
          <div className="cloud-disk-content">
            <div className="cloud-disk-upload-section">
              <div
                className={`cloud-disk-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
                {uploading ? (
                  <div className="cloud-disk-upload-progress">
                    <CloudUploadOutlined className="cloud-disk-upload-icon spinning" />
                    <p className="cloud-disk-upload-text">上传中...</p>
                    <Progress percent={uploadProgress} className="cloud-disk-progress" />
                  </div>
                ) : (
                  <>
                    <InboxOutlined className="cloud-disk-upload-icon" />
                    <p className="cloud-disk-upload-text">点击或拖拽文件到此区域上传</p>
                    <p className="cloud-disk-upload-hint">单个文件最大 {maxFileSizeMB}MB</p>
                  </>
                )}
              </div>

              <div className="cloud-disk-options">
                <div className="cloud-disk-option-item">
                  <div className="cloud-disk-option-label">
                    <LockOutlined /> 设置提取码
                    <Switch checked={enablePassword} onChange={setEnablePassword} size="small" />
                  </div>
                  {enablePassword && (
                    <Input.Password
                      placeholder="请输入提取码"
                      value={extractionCode}
                      onChange={(e) => setExtractionCode(e.target.value)}
                      maxLength={32}
                      prefix={<LockOutlined />}
                    />
                  )}
                </div>

                <div className="cloud-disk-option-item">
                  <div className="cloud-disk-option-label">
                    <SettingOutlined /> 限制下载次数
                    <Switch checked={enableMaxDownloads} onChange={setEnableMaxDownloads} size="small" />
                  </div>
                  {enableMaxDownloads && (
                    <InputNumber
                      min={1}
                      max={9999}
                      value={maxDownloads}
                      onChange={(v) => setMaxDownloads(v || 1)}
                      style={{ width: '100%' }}
                      placeholder="下载次数"
                    />
                  )}
                </div>
              </div>
            </div>

            {currentShare && (
              <div className="cloud-disk-share-result">
                <h3><LinkOutlined /> 分享已生成</h3>
                <div className="cloud-disk-share-info">
                  <div className="cloud-disk-share-row">
                    <span className="cloud-disk-share-label">文件名:</span>
                    <span className="cloud-disk-share-value" title={currentShare.originalName}>
                      <FileOutlined /> {currentShare.originalName}
                    </span>
                  </div>
                  <div className="cloud-disk-share-row">
                    <span className="cloud-disk-share-label">大小:</span>
                    <span className="cloud-disk-share-value">{formatFileSize(currentShare.fileSize)}</span>
                  </div>
                  {currentShare.extractionCode && (
                    <div className="cloud-disk-share-row">
                      <span className="cloud-disk-share-label">提取码:</span>
                      <span className="cloud-disk-share-value cloud-disk-highlight">
                        <LockOutlined /> {currentShare.extractionCode}
                      </span>
                    </div>
                  )}
                  {currentShare.maxDownloads > 0 && (
                    <div className="cloud-disk-share-row">
                      <span className="cloud-disk-share-label">下载次数:</span>
                      <span className="cloud-disk-share-value">{currentShare.maxDownloads} 次</span>
                    </div>
                  )}
                  <div className="cloud-disk-share-row">
                    <span className="cloud-disk-share-label">分享链接:</span>
                    <div className="cloud-disk-share-link">
                      <Input
                        value={currentShare.shareUrl}
                        readOnly
                        className="cloud-disk-link-input"
                      />
                      <Button
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyLink(currentShare.shareUrl)}
                      >
                        复制
                      </Button>
                      <Button
                        icon={<QrcodeOutlined />}
                        onClick={() => handleShowQR(currentShare)}
                      >
                        二维码
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="cloud-disk-records">
              <div className="cloud-disk-records-header">
                <h3>上传记录（本地缓存）</h3>
                {records.length > 0 && (
                  <Popconfirm
                    title="确定清空所有本地记录？"
                    onConfirm={() => {
                      setRecords([]);
                      saveRecords([]);
                      setCurrentShare(null);
                      message.success('已清空本地记录');
                    }}
                    okText="清空"
                    cancelText="取消"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>清空记录</Button>
                  </Popconfirm>
                )}
              </div>
              {records.length === 0 ? (
                <Empty description="暂无上传记录" />
              ) : (
                <div className="cloud-disk-records-list">
                  {records.map((record, index) => (
                    <div key={index} className="cloud-disk-record-item">
                      <div className="cloud-disk-record-info">
                        <div className="cloud-disk-record-name" title={record.originalName}>
                          <FileOutlined /> {record.originalName}
                        </div>
                        <div className="cloud-disk-record-meta">
                          <span>{formatFileSize(record.fileSize)}</span>
                          <span>{new Date(record.createdAt).toLocaleString()}</span>
                          {record.extractionCode ? (
                            <Tooltip title={`提取码: ${record.extractionCode}`}>
                              <span className="cloud-disk-tag locked"><LockOutlined /> 加密</span>
                            </Tooltip>
                          ) : (
                            <span className="cloud-disk-tag unlocked"><UnlockOutlined /> 公开</span>
                          )}
                          {record.maxDownloads > 0 && (
                            <span className="cloud-disk-tag">限{record.maxDownloads}次</span>
                          )}
                        </div>
                      </div>
                      <div className="cloud-disk-record-actions">
                        <Tooltip title="复制链接">
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyLink(record.shareUrl)}
                          />
                        </Tooltip>
                        <Tooltip title="二维码">
                          <Button
                            size="small"
                            icon={<QrcodeOutlined />}
                            onClick={() => handleShowQR(record)}
                          />
                        </Tooltip>
                        <Tooltip title="下载">
                          <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadOwn(record)}
                          />
                        </Tooltip>
                        <Tooltip title="从本地记录删除">
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteRecord(record.shareCode)}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Spin>

        {qrModalVisible && qrRecord && (
          <div className="cloud-disk-qr-modal" onClick={() => setQrModalVisible(false)}>
            <div className="cloud-disk-qr-content" onClick={(e) => e.stopPropagation()}>
              <h3>扫码下载文件</h3>
              <div className="cloud-disk-qr-code">
                <QRCode
                  value={qrRecord.shareUrl}
                  size={240}
                  errorLevel="M"
                />
              </div>
              <div className="cloud-disk-qr-info">
                <div className="cloud-disk-qr-filename" title={qrRecord.originalName}>
                  <FileOutlined /> {qrRecord.originalName}
                </div>
                <div className="cloud-disk-qr-url">{qrRecord.shareUrl}</div>
                {qrRecord.extractionCode && (
                  <div className="cloud-disk-qr-password">
                    提取码: <span className="cloud-disk-highlight">{qrRecord.extractionCode}</span>
                  </div>
                )}
              </div>
              <Button type="primary" onClick={() => setQrModalVisible(false)}>关闭</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CloudDisk;
